import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import api from '../api';

/**
 * PaymentFailureBanner
 *
 * Shows a dismissible banner at the top of client pages when the customer's
 * Stripe subscription is in a problem state (past_due / canceled / unpaid).
 *
 * Reads status from /api/company (authoritative via Stripe webhooks) and
 * falls back to /api/billing/subscription if that is missing.
 *
 * Props:
 *  - hideUpgradeLink: if true, does not render "Oppdater betaling" CTA
 *    (useful on BillingPage itself where user is already there).
 */
export default function PaymentFailureBanner({ hideUpgradeLink = false }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get('/api/company');
        if (!mounted) return;
        if (data?.payment_status) {
          setStatus(data.payment_status);
          return;
        }
      } catch {
        // Not a client user (e.g. admin) — silently ignore
      }
      try {
        const { data } = await api.get('/api/billing/subscription');
        if (!mounted) return;
        if (data?.status) setStatus(data.status);
      } catch {
        // Ignore — billing endpoint not available
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  if (dismissed) return null;
  if (!status) return null;
  const bad = ['past_due', 'unpaid', 'canceled', 'cancelled', 'incomplete', 'incomplete_expired'].includes(status);
  if (!bad) return null;

  const isCanceled = status === 'canceled' || status === 'cancelled';
  const title = isCanceled ? 'Abonnementet er avsluttet' : 'Betalingsproblem';
  const body = isCanceled
    ? 'Abonnementet ditt er avsluttet. Start det på nytt for å fortsette å bruke AI-resepsjonisten.'
    : 'Den siste betalingen din mislyktes. Oppdater betalingsmetoden din for å unngå avbrudd i tjenesten.';
  const ctaLabel = isCanceled ? 'Start abonnement' : 'Oppdater betaling';

  return (
    <div
      data-testid="payment-failure-banner"
      style={{
        background: '#FEF2F2',
        border: '1px solid #FCA5A5',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <AlertTriangle size={18} style={{ color: '#DC2626', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#DC2626', fontWeight: 700, fontSize: 14 }}>{title}</div>
        <div style={{ margin: 0, fontSize: 13, color: '#7F1D1D' }}>{body}</div>
      </div>
      {!hideUpgradeLink && (
        <button
          type="button"
          onClick={() => navigate('/billing')}
          style={{
            background: '#DC2626',
            color: 'white',
            padding: '6px 12px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            flexShrink: 0,
          }}
          data-testid="payment-failure-cta"
        >
          {ctaLabel}
        </button>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Lukk"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#991B1B',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
